import { useForm, useFormField } from "@hooks/useForm";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { $adminApi, $staffApi } from "../../../api";
import { AiDraft, Thread, ThreadSummary } from "../types";
import { useMessageFileUpload } from "./useMessageFileUpload";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import {
  getIdOfLastMessageExcludingInternalNotesAndFiles,
  getLastReadStaffMessageIndex,
  groupMessages,
} from "../threadUtil";

type GenerateType = "regenerate" | "formalize" | "elaborate" | "shorten";

type ReplyField = ReturnType<typeof useFormField<string>>;

type ThreadContextValue = {
  thread: Thread;
  aiDraft: AiDraft | null;
  threadSummary: ThreadSummary | undefined;
  handleUndoAiDraft: () => void;
  isThreadSummaryPending: boolean;
  reply: ReplyField;
  form: ReturnType<typeof useForm>;
  isSubmitting: boolean;
  setAiDraftDismissed: (dismissed: boolean) => void;
  aiDraftAvailable: boolean;
  isGeneratingReply: boolean;
  handleGenerateReply: (generateType: GenerateType, prompt?: string) => void;
  isRegenerating: boolean;
  setIsRegenerating: (value: boolean) => void;
  groupedMessages: ReturnType<typeof groupMessages>;
  lastReadStaffMessageIndex: number;
};

const ThreadContext = createContext<ThreadContextValue | null>(null);

export function ThreadProvider({ thread, children }: { thread: Thread; children: ReactNode }) {
  const { sendNotification } = useNotificationContext();
  const { features, permissions } = useUserConfiguration();
  const queryClient = useQueryClient();

  const { getFileUploadData } = useMessageFileUpload();
  const [isUploading, setIsUploading] = useState(false);

  const { data: messagesData } = $adminApi.useSuspenseQuery(
    "get",
    "/v2/admin/messages/",
    {
      params: {
        query: {
          thread: thread.id,
          ordering: ["created_at"],
        },
      },
    },
    {
      refetchInterval: 5000,
      refetchIntervalInBackground: false,
    }
  );

  const { data: aiDraftResponse } = $staffApi.useQuery(
    "get",
    "/api/threads/{uuid}/ai_draft/",
    {
      params: {
        path: { uuid: thread.id },
      },
    },
    {
      refetchInterval: 10000,
      refetchIntervalInBackground: false,
      enabled:
        features.includes("secure_messages_ai") &&
        permissions.includes("backoffice.generate_ai_message") &&
        permissions.includes("indigo.add_message"),
    }
  );

  const { data: threadSummary, isPending: isThreadSummaryPending } = $staffApi.useQuery(
    "get",
    "/api/threads/{uuid}/summary/",
    {
      params: {
        path: { uuid: thread.id },
      },
      enabled:
        features.includes("secure_messages_ai") &&
        features.includes("secure_messages_redesign") &&
        permissions.includes("indigo.view_thread"),
    }
  );

  const { mutate: cancelAiDraft } = $staffApi.useMutation(
    "put",
    "/api/ai_drafts/{uuid}/cancel_ai_draft/",
    {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ["get", "/api/threads/{uuid}/ai_draft/"] });
      },
      onError: () => {
        sendNotification({
          type: "negative",
          text: "Error undoing AI draft.",
        });
      },
    }
  );

  const groupedMessages = useMemo(
    () => groupMessages(messagesData.results),
    [messagesData.results]
  );

  const lastReadStaffMessageIndex = useMemo(
    () => getLastReadStaffMessageIndex(groupedMessages),
    [groupedMessages]
  );

  const aiDraft = useMemo(() => {
    const lastMessageId: string | null =
      getIdOfLastMessageExcludingInternalNotesAndFiles(groupedMessages);
    if (
      !aiDraftResponse ||
      lastMessageId === null ||
      aiDraftResponse.in_reply_to !== lastMessageId
    ) {
      return null;
    }
    return aiDraftResponse;
  }, [aiDraftResponse, groupedMessages]);

  const [aiDraftDismissed, setAiDraftDismissed] = useState(false);

  const [isRegenerating, setIsRegenerating] = useState(false);

  // Reset draft flags only once the new draft has actually loaded, so the stale
  // draft doesn't flash before the incoming one replaces it.
  useEffect(() => {
    if (aiDraft?.id) {
      setAiDraftDismissed(false);
      setIsRegenerating(false);
    }
  }, [aiDraft?.id]);

  const aiDraftAvailable = useMemo(
    () => !aiDraftDismissed && !!aiDraft,
    [aiDraftDismissed, aiDraft]
  );

  const reply = useFormField("");

  const formFields = useMemo(() => ({ reply }), [reply]);

  const { mutate: postMessage, isPending: isPostingMessage } = $staffApi.useMutation(
    "post",
    "/api/messages/",
    {
      onSuccess() {
        queryClient.invalidateQueries({
          queryKey: ["get", "/v2/admin/messages/"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get", "/v2/admin/threads/{uuid}/"],
        });
      },
      onError() {
        sendNotification({
          type: "negative",
          text: "Failed to send message. Please try again.",
        });
      },
    }
  );

  const handleFormSubmit = useCallback(
    (options: {
      isInternal?: boolean;
      postSuccessFn?: () => void;
      files?: File[];
      onUploaded?: () => void;
    }) => {
      const {
        isInternal = false,
        postSuccessFn = () => {},
        files = [],
        onUploaded = () => {},
      } = options;
      const replyBody = (
        aiDraftAvailable && !reply.value.trim() ? aiDraft?.body : reply.value
      )?.trim();

      if (!replyBody && files.length === 0) {
        sendNotification({
          type: "negative",
          text: "Cannot send an empty message.",
        });
        return;
      }

      // Upload the in-memory attachments to S3 only now that the message is being sent.
      void (async () => {
        try {
          setIsUploading(true);
          const uploadedFiles = await getFileUploadData(files);
          postMessage(
            {
              body: {
                thread: thread.id,
                body: replyBody,
                internal_note: isInternal,
                files: uploadedFiles,
              },
            },
            {
              onSuccess: () => {
                reply.reset();
                onUploaded();
                postSuccessFn();
              },
            }
          );
        } catch {
          sendNotification({
            type: "negative",
            text: "Failed to upload attachments.",
          });
        } finally {
          setIsUploading(false);
        }
      })();

      return true;
    },
    [aiDraft, aiDraftAvailable, getFileUploadData, postMessage, reply, sendNotification, thread.id]
  );

  const form = useForm(formFields, { onSubmit: handleFormSubmit });

  const isSubmitting = isUploading || isPostingMessage;

  const { mutate: generateReply, isPending: isGeneratingReply } = $staffApi.useMutation(
    "post",
    "/api/threads/{uuid}/generate_ai_draft_for_staff_reply/",
    {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ["get", "/api/threads/{uuid}/ai_draft/"] });
        reply.setValue("");
      },
      onError: () => {
        sendNotification({
          type: "negative",
          text: "Error generating AI response.",
        });
      },
    }
  );

  const handleGenerateReply = useCallback(
    (generateType: GenerateType, prompt: string = "") => {
      const effectivePrompt = prompt.trim()
        ? prompt
        : generateType === "regenerate"
          ? "Regenerate the response for this thread."
          : generateType === "formalize"
            ? "Make this response more formal."
            : generateType === "elaborate"
              ? "Explain this response in more detail."
              : generateType === "shorten"
                ? "Make this response shorter."
                : "";

      generateReply({
        params: {
          path: { uuid: thread.id },
        },
        body: {
          prompt: effectivePrompt,
        },
      });
    },
    [generateReply, thread.id]
  );

  const handleUndoAiDraft = useCallback(() => {
    if (aiDraft === null || aiDraft.status !== "ready") {
      sendNotification({
        type: "negative",
        text: "AI draft cannot be dismissed in this state.",
      });

      return;
    }

    cancelAiDraft({
      params: {
        path: {
          uuid: aiDraft.id,
        },
      },
    });
  }, [aiDraft, cancelAiDraft, sendNotification]);

  const value: ThreadContextValue = useMemo(
    () => ({
      thread,
      aiDraft,
      threadSummary,
      handleUndoAiDraft,
      isThreadSummaryPending,
      reply,
      form,
      isUploading,
      isPostingMessage,
      setAiDraftDismissed,
      aiDraftAvailable,
      isGeneratingReply,
      handleGenerateReply,
      isRegenerating,
      setIsRegenerating,
      groupedMessages,
      isSubmitting,
      lastReadStaffMessageIndex,
    }),
    [
      thread,
      aiDraft,
      threadSummary,
      handleUndoAiDraft,
      isThreadSummaryPending,
      reply,
      form,
      isUploading,
      isPostingMessage,
      aiDraftAvailable,
      isGeneratingReply,
      handleGenerateReply,
      isRegenerating,
      groupedMessages,
      isSubmitting,
      lastReadStaffMessageIndex,
    ]
  );

  return <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>;
}

export function useThread() {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error("useThread must be used within a ThreadProvider");
  }
  return context;
}
